import algoliasearch from 'algoliasearch/lite'

import {
  ANALYTICS_TAG_SUGGESTIONS,
  DEFAULT_PARAMS,
  HITS_PER_PAGE_SEARCH_BOX,
  SEARCHABLE_FILTERS,
} from 'shared/consts/algolia'
import { getAlgoliaNeuralSearchIndexIfEnabled } from 'shared/experiments/components/AlgoliaNeuralSearch'
import { isMarketplaceEnabledForTenant } from 'shared/experiments/utils/featureFlags'
import { validate as validateEan } from 'shared/services/api/helpers/eanValidator'
import { validate as validatePzn } from 'shared/services/api/helpers/pznValidator'
import store from 'shared/store'
import getDevicePlatform from 'shared/utils/algolia/getDevicePlatform'
import assert from 'shared/utils/assert'
import { stripLanguageFromPath } from 'shared/utils/url'

import BackendAPIService from './BackendAPIService'

class ClientAPIService extends BackendAPIService {
  constructor(props) {
    super(props)

    const { searchConfig } = props

    assert(searchConfig, 'ClientAPIService: no "searchConfig" option provided')

    this.searchConfig = searchConfig
  }

  async searchAlgolia(
    query,
    { userToken, deviceClass = null, expa = null, ruleContexts = [] }
  ) {
    assert(query, 'ClientAPIService.searchAlgolia: please provide a "query"')
    this.logger.debug(
      `Fetching Search-Autosuggestions (Algolia) for "${query}"`,
      'dataFetchingAll'
    )

    const {
      algolia: { appId, apiKey, indexName, mpIndexName },
    } = this.searchConfig

    const {
      pageProperties: { experiments },
      publicConfig: { tenant },
    } = store.getPublicRuntimeConfig()

    let toggleSwitchIndex = isMarketplaceEnabledForTenant(experiments, tenant)
      ? mpIndexName
      : indexName

    toggleSwitchIndex = getAlgoliaNeuralSearchIndexIfEnabled(
      experiments,
      toggleSwitchIndex
    )

    const ruleContextsList = []
    if (expa) {
      ruleContextsList.push(expa)
    }
    if (ruleContexts?.length) {
      ruleContextsList.push(...ruleContexts)
    }

    const params = {
      ...DEFAULT_PARAMS,
      query,
      hitsPerPage: HITS_PER_PAGE_SEARCH_BOX,
      clickAnalytics: true,
      getRankingInfo: true,
      analyticsTags: [
        ANALYTICS_TAG_SUGGESTIONS,
        getDevicePlatform(deviceClass),
      ],
      ruleContexts: ruleContextsList,
    }

    const { valid: validPzn } = validatePzn(query)
    const { valid: validEan } = validateEan(query)
    if (validPzn || validEan) {
      params.filters = SEARCHABLE_FILTERS
    }

    const searchClientConfig = {}
    if (userToken) {
      params.userToken = userToken
      params.analytics = true
      searchClientConfig.headers = {}
      searchClientConfig.headers['X-Algolia-UserToken'] = userToken
    }

    const searchClient = algoliasearch(appId, apiKey, searchClientConfig)
    const index = searchClient.initIndex(toggleSwitchIndex)

    const response = await index.search(query, params)

    this.logger.debug(
      `Search-Autosuggestions (Algolia) for "${query}" fetched!`,
      'dataFetchingAll'
    )

    return response
  }

  getLegalTextPath() {
    return `/cacheable/${this.language}/content/${this.apiVersion}/${this.tenantPath}/snippetsFooter`
  }

  async fetchLegalText() {
    this.logger.debug('Fetching LegalText', 'dataFetchingAll')
    const params = {
      contentsFilter: 'headline:legalText',
      fields: 'id title contents { headline content }',
    }
    const { status, data } = await this.get(this.getLegalTextPath(), { params })
    this.logger.debug('LegalText fetched!', 'dataFetchingAll')

    return {
      status,
      data: data.contents[0].content,
    }
  }

  getOffCanvasCategoriesByPath(categoryPath) {
    categoryPath = stripLanguageFromPath(categoryPath, this.language)
    const pathWithoutSlashes = categoryPath.replaceAll('/', '')
    return `cacheable/${this.language}/content/v3/${this.tenantPath}/nodes/${pathWithoutSlashes}`
  }

  getOffCanvasCategoriesById(id) {
    return `cacheable/${this.language}/content/v3/${this.tenantPath}/nodes-by-code/${id}`
  }

  getConvertedResponse(item) {
    const subcategories =
      item?.down?.length > 0
        ? item.down.map(cat => this.getConvertedResponse(cat))
        : []

    const mappedData = {
      'id': item?.code,
      'name': item?.caption,
      'canonicalUrl':
        store.getPublicRuntimeConfig().publicConfig.shopURL + item?.path,
      'seoName': item?.path,
      subcategories,
      'hasSubcategories': item?.child_count > 0,
    }

    return mappedData
  }

  async fetchOffCanvasCategories(categoryIdentifier, fetchByPath) {
    this.logger.debug('Fetching offcanvas categories', 'dataFetchingAll')

    const { data } = fetchByPath
      ? await this.get(this.getOffCanvasCategoriesByPath(categoryIdentifier))
      : await this.get(this.getOffCanvasCategoriesById(categoryIdentifier))

    return this.getConvertedResponse(data)
  }
}

export default ClientAPIService
